import { useMutation, useQuery } from "@apollo/client";
import { useTheme } from "@mui/material";
import { useGoogleLogin } from "@react-oauth/google";
import { useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import { LoginResult } from "../../graphql/graphql";
import { GOOGLE_LOGIN } from "../../graphql/mutations-gql";
import { HELLO } from "../../graphql/queries-gql";
import { useAuthStore } from "../../hooks/useAuth";
import GoogleButton from "./GoogleButton";

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-color: ${(props) => props.theme.bodyBackground};
`;

const LoginCard = styled.div`
  width: 380px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 20px;
  padding: 32px 28px 28px;
  background-color: ${(props) => props.theme.cardBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 14px;
`;

const Brand = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const AppIcon = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 12px;
`;

const BrandName = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const Name = styled.span`
  font-size: 20px;
  font-weight: 650;
  letter-spacing: -0.015em;
  color: ${(props) => props.theme.textColor};
`;

const Label = styled.span`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const CaptchaRow = styled.div`
  display: flex;
  justify-content: center;
  min-height: 78px;
`;

const Errors = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LoginPage = () => {
  const setAuthenticated = useAuthStore.use.setAuthenticated();
  const [googleLoginMutation, { error: googleError }] = useMutation<{
    googleLogin: LoginResult;
  }>(GOOGLE_LOGIN);
  const [socialLoginError, setSocialLoginError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [captcha, setCaptcha] = useState<string | null>(null);
  const { data } = useQuery(HELLO);
  const theme = useTheme();

  const onCaptchaChange = (value: any) => {
    console.log("captcha change", value);
    setCaptcha(value);
  };

  const loginWithGoogleToken = (access_token: string) => {
    // Try a login with google. If no user exists we show the google signup dialog that lets the user select
    // some necessary fields before signup can be completed. Signup should automatically login the user.
    googleLoginMutation({
      variables: {
        token: access_token,
        captcha: captcha,
      },
      onCompleted: (data) => {
        console.log("googleLogin onCompleted", data);
        if (data.googleLogin.authenticated && data.googleLogin.token) {
          localStorage.setItem("auth_token", data.googleLogin.token);
          setAuthenticated(true);
          navigate("/profile-photos");
        }
      },
      onError: (error) => {
        console.log("googleLogin onError", error);
      },
    });
  };

  const googleLogin = useGoogleLogin({
    scope: "https://www.googleapis.com/auth/userinfo.profile",
    onSuccess: (tokenResponse) => {
      console.log(tokenResponse);

      // access_token
      // token_type
      if (tokenResponse) {
        loginWithGoogleToken(tokenResponse.access_token);
      } else {
        setSocialLoginError("No valid response received from Google.");
      }
    },
  });

  return (
    <PageContainer>
      <LoginCard>
        <Brand>
          <AppIcon src={`${process.env.PUBLIC_URL}/logo-small.png`} alt="" />
          <BrandName>
            <Name>Minglify</Name>
            <Label>Backoffice</Label>
          </BrandName>
        </Brand>
        <CaptchaRow>
          <ReCAPTCHA
            className="recaptcha-class"
            theme={theme.name === "dark" ? "dark" : "light"}
            sitekey="6Lfzc-4SAAAAAByyUavMBhn0QmfShVQunuiCupto"
            onChange={(value: any) => onCaptchaChange(value)}
          />
        </CaptchaRow>
        <GoogleButton onClick={() => googleLogin()} />
        {(googleError || socialLoginError) && (
          <Errors>
            {googleError && <ErrorAlert error={googleError} />}
            {socialLoginError && <ErrorAlert error={socialLoginError} />}
          </Errors>
        )}
      </LoginCard>
    </PageContainer>
  );
};
export default LoginPage;
